// The Agents Inbox GATE, and the four reads it gates.
//
// This is what `src/features/inbox/hooks.ts` was, arriving behind the door with
// its expressions unchanged. That module survived four carves as the one bodied
// delegate under a locked root, for a stated reason: the headless reads take an
// `enabled` parameter, and the four inputs that composed it were all app state.
// The Heidi Work verdict and identity hydration stay injected. Authentication
// and the overlay token-sync read come from `@heidi/auth/state`. `useUser()`
// still cannot be imported here: a foreign domain's non-`/contracts` door is
// banned from `/data`.
//
// TWO GATES, NOT ONE WITH A PARAMETER. The base gate demands a hydrated
// identity; the run-scoped gate accepts the overlay's token-sync in its place.
// Collapsing them would either fire the global pending-inbox list in the
// overlay or close the run-scoped reads there, and the second is the bug the
// overlay branch was added for.
import { useIsAuthTokenSynced } from '@heidi/auth/state';

import type { InboxItemsParams, PendingToolApproval } from '../../contracts';
import { injected, useIsAuthenticated } from '../injected-auth';
import { useIsIdentityHydrated } from '../injected-identity';
import {
  useInboxItems as useInboxItemsCore,
  useInboxItemsForRun as useInboxItemsForRunCore,
  usePendingApprovalRunIds as usePendingApprovalRunIdsCore,
  usePendingApprovalsForRun as usePendingApprovalsForRunCore,
  useProjectIdsNeedingAttention as useProjectIdsNeedingAttentionCore,
} from '../query/useInboxItems';

const useIsHeidiWorkEnabled = (): boolean => injected('useIsHeidiWorkEnabled')();

/**
 * The base gate. Backend gates `/inbox/*` behind the same PostHog-OR-Flagsmith
 * umbrella (`is_heidi_work_enabled`) — un-flagged users get 404, so the two
 * must stay in parity — and we wait for the identity to hydrate so we don't
 * fire with a stale boot-window token (INC-215).
 */
const useInboxEnabled = (): boolean => {
  const isHeidiWorkEnabled = useIsHeidiWorkEnabled();
  const isAuthenticated = useIsAuthenticated();
  const isIdentityHydrated = useIsIdentityHydrated();
  return isHeidiWorkEnabled && isAuthenticated && isIdentityHydrated;
};

/**
 * The run-scoped gate. Same shape as `useInboxEnabled` plus the overlay branch:
 * in the main app a hydrated identity confirms auth is bootstrapped (INC-215
 * protection); in the overlay `CurrentUserProvider` is absent so the identity
 * never hydrates and a freshly synced token from the main window stands in.
 * Stated once so `useInboxItemsForRun` and `usePendingApprovalsForRun` share
 * it — the pre-carve code duplicated it between the two.
 */
const useRunScopedInboxEnabled = (): boolean => {
  const isHeidiWorkEnabled = useIsHeidiWorkEnabled();
  const isAuthenticated = useIsAuthenticated();
  const isIdentityHydrated = useIsIdentityHydrated();
  const isAuthTokenSynced = useIsAuthTokenSynced();
  return (
    isHeidiWorkEnabled && isAuthenticated && (isIdentityHydrated || isAuthTokenSynced)
  );
};

export const useInboxItems = (params?: InboxItemsParams) => {
  const enabled = useInboxEnabled();
  return useInboxItemsCore({ params, enabled });
};

export const useInboxItemsForRun = (agentRunId: string | null | undefined) =>
  useInboxItemsForRunCore({ agentRunId, enabled: useRunScopedInboxEnabled() });

export function usePendingApprovalsForRun(
  agentRunId: string | null | undefined
): PendingToolApproval[];
export function usePendingApprovalsForRun(
  agentRunIds: Array<string | null | undefined>,
  enabled?: boolean
): ReadonlySet<string>;
export function usePendingApprovalsForRun(
  agentRunIdOrIds: string | null | undefined | Array<string | null | undefined>,
  enabled = true
): PendingToolApproval[] | ReadonlySet<string> {
  const inboxEnabled = useRunScopedInboxEnabled() && enabled;
  const isRunList = Array.isArray(agentRunIdOrIds);
  const approvals = usePendingApprovalsForRunCore({
    agentRunId: isRunList ? undefined : agentRunIdOrIds,
    enabled: inboxEnabled && !isRunList,
  });
  const approvalRunIds = usePendingApprovalRunIdsCore({
    agentRunIds: isRunList ? agentRunIdOrIds : [],
    enabled: inboxEnabled && isRunList,
  });

  return isRunList ? approvalRunIds : approvals;
}

/**
 * THE CALLER GATE IS OPTIONAL AND IS ANDed, never substituted for the gate.
 * Three call sites want the attention set unconditionally and pass nothing;
 * `src/pages/scribe/chat/projects/[[...id]].tsx` wants it only on the Work
 * Home landing and passes its own `enabled`, which it used to give the
 * headless core directly — the core is package-internal now, so the caller
 * gate arrives here instead. Same shape as `usePendingApprovalsForRun`'s list
 * overload, which has ANDed a caller's `enabled` with this gate all along.
 */
export const useProjectIdsNeedingAttention = (options?: {
  enabled?: boolean;
}): Set<string> => {
  const enabled = useInboxEnabled();
  return useProjectIdsNeedingAttentionCore({
    enabled: enabled && (options?.enabled ?? true),
  });
};
